import { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  Columns3,
  MapPin,
  MessageCircle,
  Search,
  User,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { getSportLabel } from "@/lib/sport-defaults";
import {
  getDataSourceBadgeClass,
  getDataSourceLabel,
} from "@/lib/data-source";
import { ProposedStationDrawer } from "./ProposedStationDrawer";
import { StationPhotoLightbox } from "./StationPhotoLightbox";

type PilRole = "main" | "backup" | "none";
type CityVerdict = "approve" | "reject" | "needs_change";

export interface ProposedStationRow {
  id: string;
  label: string;
  location_id: string;
  location_name: string;
  address: string | null;
  location_source: string | null;
  role: PilRole;
  city_verdict: CityVerdict | null;
  city_verdict_at: string | null;
  city_verdict_source: "city" | "admin" | null;
  admin_tier: number | null;
  admin_notes: string | null;
  placed_by: string | null;
  created_at: string;
  approved_at: string | null;
  comment_count: number;
  photos: Array<{
    id: string;
    url: string;
    thumb_url: string;
    captured_at: string | null;
  }>;
  lockers: Array<{ locker_index: number; sport_type: string | null }>;
  sports: string[];
}

interface ProposedStationsTableProps {
  collectionId: string;
  rows: ProposedStationRow[];
  isLoading: boolean;
}

const VERDICT_STYLES: Record<CityVerdict, string> = {
  approve: "border-emerald-200 bg-emerald-50 text-emerald-700",
  reject: "border-rose-200 bg-rose-50 text-rose-700",
  needs_change: "border-amber-200 bg-amber-50 text-amber-700",
};

const VERDICT_LABEL: Record<CityVerdict, string> = {
  approve: "Approved",
  reject: "Rejected",
  needs_change: "Needs change",
};

const ROLE_STYLES: Record<PilRole, string> = {
  main: "bg-primary/10 text-primary border-primary/20",
  backup: "bg-muted text-muted-foreground",
  none: "bg-muted/40 text-muted-foreground",
};

const formatSport = getSportLabel;

function formatRelative(iso: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const day = 1000 * 60 * 60 * 24;
  if (diff < day) return "today";
  if (diff < day * 2) return "yesterday";
  if (diff < day * 30) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function truncate(s: string | null, n: number): string {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function ProposedStationsTable({
  collectionId,
  rows,
  isLoading,
}: ProposedStationsTableProps) {
  const utils = trpc.useUtils();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "role", desc: false },
    { id: "admin_tier", desc: false },
    { id: "label", desc: false },
  ]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    dates: false,
    placed_by: false,
    notes: false,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    photos: ProposedStationRow["photos"];
    initialIndex: number;
  } | null>(null);

  const setRole = trpc.physicalMapping.setPilRole.useMutation({
    onSuccess: () => {
      utils.physicalMapping.listApprovedStations.invalidate({ collectionId });
      utils.physicalMapping.getCityReviewSummary.invalidate({ collectionId });
    },
    onError: (e) => toast.error(e.message),
  });

  const setTier = trpc.physicalMapping.setAdminTier.useMutation({
    onSuccess: () => {
      utils.physicalMapping.listApprovedStations.invalidate({ collectionId });
    },
    onError: (e) => toast.error(e.message),
  });

  const setVerdict = trpc.physicalMapping.setCityVerdictByAdmin.useMutation({
    onSuccess: () => {
      utils.physicalMapping.listApprovedStations.invalidate({ collectionId });
      utils.physicalMapping.getCityReviewSummary.invalidate({ collectionId });
    },
    onError: (e) => toast.error(e.message),
  });

  const columns = useMemo<ColumnDef<ProposedStationRow>[]>(
    () => [
      {
        id: "photos",
        header: "Photos",
        cell: ({ row }) => {
          const photos = row.original.photos;
          const shown = photos.slice(0, 3);
          const extra = photos.length - shown.length;
          return (
            <div className="flex items-center gap-1">
              {shown.length === 0 && (
                <span className="text-xs text-muted-foreground">—</span>
              )}
              {shown.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  className="size-9 rounded overflow-hidden border bg-muted hover:ring-2 hover:ring-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox({ photos, initialIndex: idx });
                  }}
                >
                  <img
                    src={p.thumb_url}
                    alt=""
                    className="size-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
              {extra > 0 && (
                <button
                  type="button"
                  className="size-9 rounded border bg-muted text-[11px] font-medium text-muted-foreground hover:ring-2 hover:ring-primary flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox({ photos, initialIndex: shown.length });
                  }}
                >
                  +{extra}
                </button>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "label",
        accessorFn: (r) => `${r.label} ${r.address ?? ""}`,
        header: ({ column }) => (
          <SortableHeader
            label="Station"
            dir={column.getIsSorted()}
            onClick={() => column.toggleSorting()}
          />
        ),
        cell: ({ row }) => (
          <div className="min-w-[160px]">
            <div className="font-medium truncate">{row.original.label}</div>
            {row.original.address && (
              <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                <MapPin className="size-3 shrink-0" />
                {row.original.address}
              </div>
            )}
          </div>
        ),
        sortingFn: (a, b) => a.original.label.localeCompare(b.original.label),
      },
      {
        id: "sports",
        accessorFn: (r) => r.sports.join(","),
        header: "Sports",
        cell: ({ row }) => {
          const sports = row.original.sports;
          if (sports.length === 0) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1 max-w-[200px]">
              {sports.map((s) => (
                <Badge
                  key={s}
                  variant="outline"
                  className="text-[10px] font-normal capitalize"
                >
                  {formatSport(s)}
                </Badge>
              ))}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "data_source",
        accessorFn: (r) => r.location_source ?? "",
        header: "Data source",
        cell: ({ row }) => {
          const src = row.original.location_source;
          return (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] font-normal capitalize",
                getDataSourceBadgeClass(src),
              )}
            >
              {getDataSourceLabel(src)}
            </Badge>
          );
        },
      },
      {
        id: "admin_tier",
        accessorFn: (r) => r.admin_tier ?? 99,
        header: ({ column }) => (
          <SortableHeader
            label="Tier"
            dir={column.getIsSorted()}
            onClick={() => column.toggleSorting()}
          />
        ),
        cell: ({ row }) => {
          const tier = row.original.admin_tier ?? null;
          return (
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={tier ? String(tier) : ""}
              onValueChange={(v) => {
                if (!v) return;
                setTier.mutate({
                  pilId: row.original.id,
                  tier: Number(v) as 1 | 2 | 3,
                });
              }}
              disabled={setTier.isPending}
              onClick={(e) => e.stopPropagation()}
            >
              {([1, 2, 3] as const).map((t) => (
                <ToggleGroupItem
                  key={t}
                  value={String(t)}
                  className="h-6 w-6 p-0 text-[11px]"
                  aria-label={`Tier ${t}`}
                >
                  {t}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          );
        },
      },
      {
        id: "role",
        accessorFn: (r) => ({ main: 0, backup: 1, none: 2 })[r.role],
        header: ({ column }) => (
          <SortableHeader
            label="Role"
            dir={column.getIsSorted()}
            onClick={() => column.toggleSorting()}
          />
        ),
        cell: ({ row }) => (
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={row.original.role}
            onValueChange={(v) => {
              if (!v) return;
              setRole.mutate({
                pilId: row.original.id,
                role: v as PilRole,
              });
            }}
            disabled={setRole.isPending}
            onClick={(e) => e.stopPropagation()}
          >
            <ToggleGroupItem value="main" className="h-7 px-2 text-xs">
              Main
            </ToggleGroupItem>
            <ToggleGroupItem value="backup" className="h-7 px-2 text-xs">
              Backup
            </ToggleGroupItem>
            <ToggleGroupItem value="none" className="h-7 px-2 text-xs">
              None
            </ToggleGroupItem>
          </ToggleGroup>
        ),
        filterFn: (row, _id, filterValue: string) => {
          if (!filterValue || filterValue === "all") return true;
          return row.original.role === filterValue;
        },
      },
      {
        id: "city_verdict",
        accessorFn: (r) => r.city_verdict ?? "none",
        header: "City verdict",
        cell: ({ row }) => {
          const { city_verdict, city_verdict_source } = row.original;
          return (
            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <ToggleGroup
                type="single"
                size="sm"
                variant="outline"
                value={city_verdict ?? ""}
                onValueChange={(v) => {
                  setVerdict.mutate({
                    pilId: row.original.id,
                    verdict: (v as CityVerdict) || null,
                  });
                }}
                disabled={setVerdict.isPending}
              >
                <ToggleGroupItem
                  value="approve"
                  className={cn(
                    "h-7 px-2 text-xs",
                    city_verdict === "approve" &&
                      "bg-emerald-100 text-emerald-800",
                  )}
                >
                  ✓
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="needs_change"
                  className={cn(
                    "h-7 px-2 text-xs",
                    city_verdict === "needs_change" &&
                      "bg-amber-100 text-amber-800",
                  )}
                >
                  ?
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="reject"
                  className={cn(
                    "h-7 px-2 text-xs",
                    city_verdict === "reject" && "bg-rose-100 text-rose-800",
                  )}
                >
                  ✗
                </ToggleGroupItem>
              </ToggleGroup>
              {city_verdict && city_verdict_source === "admin" && (
                <Badge
                  variant="outline"
                  className="text-[9px] font-normal px-1 py-0 gap-0.5"
                  title="Recorded by admin on city's behalf"
                >
                  <UserCog className="size-2.5" />
                  admin
                </Badge>
              )}
            </div>
          );
        },
        filterFn: (row, _id, filterValue: string) => {
          if (!filterValue || filterValue === "all") return true;
          if (filterValue === "none") return row.original.city_verdict === null;
          return row.original.city_verdict === filterValue;
        },
      },
      {
        id: "comment_count",
        accessorFn: (r) => r.comment_count,
        header: ({ column }) => (
          <SortableHeader
            label="Comments"
            dir={column.getIsSorted()}
            onClick={() => column.toggleSorting()}
          />
        ),
        cell: ({ row }) => {
          const n = row.original.comment_count;
          return (
            <div className="flex items-center gap-1 text-xs">
              <MessageCircle
                className={cn(
                  "size-3.5",
                  n > 0 ? "text-foreground" : "text-muted-foreground",
                )}
              />
              {n}
            </div>
          );
        },
      },
      {
        id: "dates",
        accessorFn: (r) => r.created_at,
        header: ({ column }) => (
          <SortableHeader
            label="Approved"
            dir={column.getIsSorted()}
            onClick={() => column.toggleSorting()}
          />
        ),
        cell: ({ row }) => (
          <div className="text-[11px] text-muted-foreground">
            <div>{formatRelative(row.original.approved_at)}</div>
            <div className="text-[10px]">
              added {formatRelative(row.original.created_at)}
            </div>
          </div>
        ),
      },
      {
        id: "placed_by",
        accessorFn: (r) => r.placed_by ?? "",
        header: "By",
        cell: ({ row }) => (
          <Badge variant="outline" className="text-[10px] gap-1 capitalize">
            <User className="size-2.5" />
            {row.original.placed_by ?? "—"}
          </Badge>
        ),
      },
      {
        id: "notes",
        accessorFn: (r) => r.admin_notes ?? "",
        header: "Notes",
        cell: ({ row }) => {
          const n = row.original.admin_notes;
          if (!n) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <span className="text-xs text-muted-foreground">
              {truncate(n, 60)}
            </span>
          );
        },
        enableSorting: false,
      },
    ],
    [setRole, setTier, setVerdict],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _id, value: string) => {
      if (!value) return true;
      const needle = value.toLowerCase();
      return (
        row.original.label.toLowerCase().includes(needle) ||
        (row.original.address ?? "").toLowerCase().includes(needle)
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const roleFilterValue =
    (columnFilters.find((f) => f.id === "role")?.value as string) ?? "all";
  const verdictFilterValue =
    (columnFilters.find((f) => f.id === "city_verdict")?.value as string) ??
    "all";

  const setColumnFilter = (columnId: string, value: string) => {
    setColumnFilters((prev) => {
      const filtered = prev.filter((f) => f.id !== columnId);
      if (value === "all") return filtered;
      return [...filtered, { id: columnId, value }];
    });
  };

  const visibleColumns = table
    .getAllLeafColumns()
    .filter((c) => c.id !== "photos" && c.id !== "label");

  const selectedRow = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search station or address…"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="h-8 pl-7 w-[240px] text-xs"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Role:</span>
          {(["all", "main", "backup", "none"] as const).map((r) => (
            <Button
              key={r}
              variant={roleFilterValue === r ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs capitalize"
              onClick={() => setColumnFilter("role", r)}
            >
              {r}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Verdict:</span>
          {(
            [
              { v: "all", label: "All" },
              { v: "approve", label: "Approved" },
              { v: "needs_change", label: "Needs change" },
              { v: "reject", label: "Rejected" },
              { v: "none", label: "Not reviewed" },
            ] as const
          ).map(({ v, label }) => (
            <Button
              key={v}
              variant={verdictFilterValue === v ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setColumnFilter("city_verdict", v)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            {table.getRowModel().rows.length} of {rows.length}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Columns3 className="size-3.5 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {visibleColumns.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={col.getIsVisible()}
                  onCheckedChange={(val) => col.toggleVisibility(!!val)}
                  className="capitalize"
                >
                  {col.id.replace(/_/g, " ")}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <TableHead
                    key={h.id}
                    className="text-[11px] uppercase tracking-wide"
                  >
                    {h.isPlaceholder
                      ? null
                      : flexRender(
                          h.column.columnDef.header,
                          h.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {table.getAllLeafColumns().map((c) =>
                    c.getIsVisible() ? (
                      <TableCell key={c.id}>
                        <Skeleton className="h-5 w-24" />
                      </TableCell>
                    ) : null,
                  )}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={table.getAllLeafColumns().length}
                  className="h-20 text-center text-sm text-muted-foreground"
                >
                  No stations match this filter.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const role = row.original.role;
                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "cursor-pointer hover:bg-muted/40",
                      role === "main" && "bg-primary/[0.03]",
                    )}
                    onClick={() => setSelectedId(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-2 align-middle">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {selectedRow && (
        <ProposedStationDrawer
          collectionId={collectionId}
          station={selectedRow}
          open={!!selectedId}
          onOpenChange={(o: boolean) => {
            if (!o) setSelectedId(null);
          }}
          onOpenLightbox={(initialIndex: number) =>
            setLightbox({ photos: selectedRow.photos, initialIndex })
          }
        />
      )}

      {lightbox && (
        <StationPhotoLightbox
          photos={lightbox.photos}
          initialIndex={lightbox.initialIndex}
          open={!!lightbox}
          onOpenChange={(o: boolean) => {
            if (!o) setLightbox(null);
          }}
        />
      )}
    </div>
  );
}

function SortableHeader({
  label,
  dir,
  onClick,
}: {
  label: string;
  dir: false | "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 hover:text-foreground"
    >
      {label}
      <ArrowUpDown
        className={cn(
          "size-3",
          dir ? "text-foreground" : "text-muted-foreground/50",
        )}
      />
    </button>
  );
}

// Re-export VERDICT helpers for drawer
export { VERDICT_STYLES, VERDICT_LABEL, ROLE_STYLES, formatSport, formatRelative };
